import { useState } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, Select, Space, Tag, Popconfirm, message, Tabs } from "antd";
import { Plus, Package, ArrowDownUp, Truck, Boxes, BarChart3 } from "lucide-react";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import MyPagination from "../../components/Pagination";
import {
  useSupplierList, useSupplierCreate, useSupplierUpdate, useSupplierDelete,
  usePartCategoryList, usePartCategoryCreate, usePartCategoryUpdate, usePartCategoryDelete,
  usePartList, usePartCreate, usePartUpdate, usePartDelete,
  useStockLocationList, useStockLocationCreate, useStockLocationUpdate, useStockLocationDelete,
  useStockLevelList,
  useStockMovementList, useStockMovementCreate,
} from "../../../hooks/api/inventory/hooks";
import {
  ISupplier, ISupplierCreatePayload, ISupplierUpdatePayload,
  IPartCategory, IPartCategoryCreatePayload,
  IPart, IPartCreatePayload, IPartUpdatePayload,
  IStockLocation, IStockLocationCreatePayload,
  IStockLevel,
  TMovementType,
} from "../../../types/api/inventory";

const DEFAULT_NUQS_CONFIG = { throttleMs: Infinity };
const MOVEMENT_COLOR: Record<TMovementType, string> = { purchase: "green", job_usage: "blue", transfer: "purple", adjustment: "orange", return: "cyan", write_off: "red" };

// ── Suppliers Tab ──────────────────────────────────────────────────────────
function SuppliersTab() {
  const [editing, setEditing] = useState<ISupplier | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data, isLoading, refetchWithQuery } = useSupplierList({ page: 1, limit: 20 });
  const items: ISupplier[] = (data as any)?.data ?? [];
  const qData = (data as any)?.qData ?? { page: 1, limit: 20 };
  const total = qData?.total ?? 0;
  const createMut = useSupplierCreate({ onSuccess: () => { msgApi.success("Supplier created"); setModalOpen(false); form.resetFields(); refetchWithQuery({ page: 1 }); }, onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });
  const updateMut = useSupplierUpdate({ onSuccess: () => { msgApi.success("Supplier updated"); setModalOpen(false); refetchWithQuery({ page: 1 }); }, onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });
  const deleteMut = useSupplierDelete({ onSuccess: () => { msgApi.success("Supplier deleted"); refetchWithQuery({ page: 1 }); } });

  const onFinish = (vals: any) => {
    if (editing) { updateMut.mutate({ ...vals, id: editing.id } as ISupplierUpdatePayload); }
    else { createMut.mutate(vals as ISupplierCreatePayload); }
  };

  const columns: ColumnType<ISupplier>[] = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="font-medium text-slate-800">{v}</span> },
    { title: "Contact", dataIndex: "contact_name", key: "contact_name", render: (v) => v || "—" },
    { title: "Email", dataIndex: "email", key: "email", render: (v) => v || "—" },
    { title: "Phone", dataIndex: "phone", key: "phone", render: (v) => v || "—" },
    { title: "City", dataIndex: "city", key: "city", render: (v) => v || "—" },
    { title: "Payment Terms", dataIndex: "payment_terms_days", key: "payment_terms_days", render: (v) => `${v} days` },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete supplier?" onConfirm={() => deleteMut.mutate(r.id)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Suppliers ({total})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); form.resetFields(); setModalOpen(true); }} className="flex items-center gap-2">Add Supplier</ButtonWrapper>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading} pagination={false} scroll={{ x: "max-content" }}
        footer={() => <MyPagination qData={qData} total={total} DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG} onChange={refetchWithQuery} />}
      />
      <Modal title={editing ? "Edit Supplier" : "Add Supplier"} open={modalOpen} onCancel={() => { setModalOpen(false); form.resetFields(); }} footer={null} width={600}>
        <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="name" label="Company Name" rules={[{ required: true }]} className="col-span-2"><Input /></Form.Item>
            <Form.Item name="contact_name" label="Contact Name"><Input /></Form.Item>
            <Form.Item name="email" label="Email"><Input type="email" /></Form.Item>
            <Form.Item name="phone" label="Phone"><Input /></Form.Item>
            <Form.Item name="vat_number" label="VAT Number"><Input /></Form.Item>
            <Form.Item name="account_ref" label="Account Ref"><Input /></Form.Item>
            <Form.Item name="payment_terms_days" label="Payment Terms (days)" initialValue={30}><InputNumber className="w-full" min={0} /></Form.Item>
            <Form.Item name="address_line1" label="Address" className="col-span-2"><Input /></Form.Item>
            <Form.Item name="city" label="City"><Input /></Form.Item>
            <Form.Item name="postcode" label="Postcode"><Input /></Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending || updateMut.isPending}>{editing ? "Save Changes" : "Add Supplier"}</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

// ── Parts Tab ──────────────────────────────────────────────────────────────
function PartsTab() {
  const [editing, setEditing] = useState<IPart | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: catData } = usePartCategoryList({});
  const categories: IPartCategory[] = (catData as any)?.data ?? [];
  const { data, isLoading, refetchWithQuery } = usePartList({ page: 1, limit: 20 });
  const items: IPart[] = (data as any)?.data ?? [];
  const qData = (data as any)?.qData ?? { page: 1, limit: 20 };
  const total = qData?.total ?? 0;
  const createMut = usePartCreate({ onSuccess: () => { msgApi.success("Part created"); setModalOpen(false); form.resetFields(); refetchWithQuery({ page: 1 }); }, onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });
  const updateMut = usePartUpdate({ onSuccess: () => { msgApi.success("Part updated"); setModalOpen(false); refetchWithQuery({ page: 1 }); }, onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });
  const deleteMut = usePartDelete({ onSuccess: () => { msgApi.success("Part deleted"); refetchWithQuery({ page: 1 }); } });

  const onFinish = (vals: any) => {
    if (editing) { updateMut.mutate({ ...vals, id: editing.id } as IPartUpdatePayload); }
    else { createMut.mutate(vals as IPartCreatePayload); }
  };

  const columns: ColumnType<IPart>[] = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="font-medium text-slate-800">{v}</span> },
    { title: "SKU", dataIndex: "sku", key: "sku", render: (v) => v ? <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{v}</span> : "—" },
    { title: "Category", dataIndex: "category_name", key: "category_name", render: (v) => v || "—" },
    { title: "Supplier", dataIndex: "supplier_name", key: "supplier_name", render: (v) => v || "—" },
    { title: "Unit", dataIndex: "unit", key: "unit" },
    { title: "Cost", dataIndex: "unit_cost", key: "unit_cost", render: (v) => `£${Number(v).toFixed(2)}` },
    { title: "Price", dataIndex: "unit_price", key: "unit_price", render: (v) => `£${Number(v).toFixed(2)}` },
    { title: "Reorder At", dataIndex: "reorder_point", key: "reorder_point" },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete part?" onConfirm={() => deleteMut.mutate(r.id)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Parts ({total})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); form.resetFields(); setModalOpen(true); }} className="flex items-center gap-2">Add Part</ButtonWrapper>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading} pagination={false} scroll={{ x: "max-content" }}
        footer={() => <MyPagination qData={qData} total={total} DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG} onChange={refetchWithQuery} />}
      />
      <Modal title={editing ? "Edit Part" : "Add Part"} open={modalOpen} onCancel={() => { setModalOpen(false); form.resetFields(); }} footer={null} width={640}>
        <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="name" label="Part Name" rules={[{ required: true }]} className="col-span-2"><Input /></Form.Item>
            <Form.Item name="sku" label="SKU"><Input placeholder="Stock code" /></Form.Item>
            <Form.Item name="barcode" label="Barcode"><Input /></Form.Item>
            <Form.Item name="category_id" label="Category">
              <Select allowClear options={categories.map((c) => ({ value: c.id, label: c.name }))} />
            </Form.Item>
            <Form.Item name="unit" label="Unit" initialValue="each"><Input /></Form.Item>
            <Form.Item name="unit_cost" label="Unit Cost (£)" initialValue={0}><InputNumber className="w-full" min={0} step={0.01} prefix="£" /></Form.Item>
            <Form.Item name="unit_price" label="Unit Price (£)" initialValue={0}><InputNumber className="w-full" min={0} step={0.01} prefix="£" /></Form.Item>
            <Form.Item name="vat_rate_pct" label="VAT %" initialValue={20}><InputNumber className="w-full" min={0} max={100} /></Form.Item>
            <Form.Item name="reorder_point" label="Reorder Point" initialValue={0}><InputNumber className="w-full" min={0} /></Form.Item>
            <Form.Item name="reorder_qty" label="Reorder Qty" initialValue={0}><InputNumber className="w-full" min={0} /></Form.Item>
          </div>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending || updateMut.isPending}>{editing ? "Save Changes" : "Add Part"}</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

// ── Stock Locations Tab ────────────────────────────────────────────────────
function StockLocationsTab() {
  const [editing, setEditing] = useState<IStockLocation | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data, isLoading, refetch } = useStockLocationList({});
  const items: IStockLocation[] = (data as any)?.data ?? [];
  const createMut = useStockLocationCreate({ onSuccess: () => { msgApi.success("Location created"); setModalOpen(false); form.resetFields(); refetch(); } });
  const updateMut = useStockLocationUpdate({ onSuccess: () => { msgApi.success("Location updated"); setModalOpen(false); refetch(); } });
  const deleteMut = useStockLocationDelete({ onSuccess: () => { msgApi.success("Location deleted"); refetch(); } });

  const onFinish = (vals: any) => {
    if (editing) { updateMut.mutate({ ...vals, id: editing.id }); }
    else { createMut.mutate(vals as IStockLocationCreatePayload); }
  };

  const LOCATION_COLOR: Record<string, string> = { warehouse: "blue", van: "orange", site: "green", office: "purple" };
  const columns: ColumnType<IStockLocation>[] = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="font-medium text-slate-800">{v}</span> },
    { title: "Type", dataIndex: "location_type", key: "location_type", render: (v) => <Tag color={LOCATION_COLOR[v]}>{v}</Tag> },
    { title: "Branch", dataIndex: "branch_name", key: "branch_name", render: (v) => v || "—" },
    { title: "Address", dataIndex: "address_line1", key: "address_line1", render: (v) => v || "—" },
    { title: "Postcode", dataIndex: "postcode", key: "postcode", render: (v) => v || "—" },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete location?" onConfirm={() => deleteMut.mutate(r.id)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Stock Locations ({items.length})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); form.resetFields(); setModalOpen(true); }} className="flex items-center gap-2">Add Location</ButtonWrapper>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading} pagination={false} scroll={{ x: "max-content" }} />
      <Modal title={editing ? "Edit Location" : "Add Location"} open={modalOpen} onCancel={() => { setModalOpen(false); form.resetFields(); }} footer={null} width={520}>
        <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="location_type" label="Type" initialValue="warehouse">
            <Select options={[{ value: "warehouse", label: "Warehouse" }, { value: "van", label: "Van" }, { value: "site", label: "Site" }, { value: "office", label: "Office" }]} />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="address_line1" label="Address"><Input /></Form.Item>
            <Form.Item name="postcode" label="Postcode"><Input /></Form.Item>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending || updateMut.isPending}>{editing ? "Save" : "Add Location"}</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

// ── Stock Levels Tab ───────────────────────────────────────────────────────
function StockLevelsTab() {
  const { data, isLoading } = useStockLevelList({});
  const items: IStockLevel[] = (data as any)?.data ?? [];

  const columns: ColumnType<IStockLevel>[] = [
    { title: "Part", dataIndex: "part_name", key: "part_name", render: (v) => <span className="font-medium text-slate-800">{v || "—"}</span> },
    { title: "SKU", dataIndex: "sku", key: "sku", render: (v) => v ? <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{v}</span> : "—" },
    { title: "Location", dataIndex: "location_name", key: "location_name", render: (v) => v || "—" },
    {
      title: "In Stock", dataIndex: "quantity", key: "quantity",
      render: (v) => <span className={`font-bold ${Number(v) <= 0 ? "text-red-500" : Number(v) <= 5 ? "text-amber-600" : "text-emerald-600"}`}>{Number(v)}</span>,
    },
    {
      title: "Reserved", dataIndex: "reserved_qty", key: "reserved_qty",
      render: (v) => <span className="text-slate-500">{Number(v ?? 0)}</span>,
    },
    {
      title: "Available", key: "available",
      render: (_, r) => {
        const avail = Number(r.quantity) - Number(r.reserved_qty ?? 0);
        return <span className={`font-semibold ${avail <= 0 ? "text-red-500" : "text-slate-700"}`}>{avail}</span>;
      },
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Stock Levels ({items.length})</h2>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading} pagination={false} scroll={{ x: "max-content" }} />
    </>
  );
}

// ── Stock Movements Tab ────────────────────────────────────────────────────
function StockMovementsTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data, isLoading, refetchWithQuery } = useStockMovementList({ page: 1, limit: 20 });
  const items = (data as any)?.data ?? [];
  const qData = (data as any)?.qData ?? { page: 1, limit: 20 };
  const total = qData?.total ?? 0;
  const { data: locData } = useStockLocationList({});
  const locations = (locData as any)?.data ?? [];
  const { data: partsData } = usePartList({ page: 1, limit: 500 });
  const allParts: IPart[] = (partsData as any)?.data ?? [];
  const createMut = useStockMovementCreate({ onSuccess: () => { msgApi.success("Movement recorded"); setModalOpen(false); form.resetFields(); refetchWithQuery({ page: 1 }); }, onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });

  const columns: ColumnType<any>[] = [
    { title: "Date", dataIndex: "created_at", key: "created_at", render: (v) => dayjs(v).format("DD MMM YYYY HH:mm") },
    { title: "Part", dataIndex: "part_name", key: "part_name" },
    { title: "Type", dataIndex: "movement_type", key: "movement_type", render: (v: TMovementType) => <Tag color={MOVEMENT_COLOR[v]}>{v.replace("_", " ")}</Tag> },
    { title: "Qty", dataIndex: "quantity", key: "quantity", render: (v) => <span className="font-semibold">{v}</span> },
    { title: "From", dataIndex: "from_location_id", key: "from_location_id", render: (v) => locations.find((l: any) => l.id === v)?.name || (v ? `#${v}` : "—") },
    { title: "To", dataIndex: "to_location_id", key: "to_location_id", render: (v) => locations.find((l: any) => l.id === v)?.name || (v ? `#${v}` : "—") },
    { title: "By", dataIndex: "created_by_name", key: "created_by_name", render: (v) => v?.trim() || "—" },
    { title: "Notes", dataIndex: "notes", key: "notes", render: (v) => v || "—" },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Stock Movements ({total})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => setModalOpen(true)} className="flex items-center gap-2">Record Movement</ButtonWrapper>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading} pagination={false} scroll={{ x: "max-content" }}
        footer={() => <MyPagination qData={qData} total={total} DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG} onChange={refetchWithQuery} />}
      />
      <Modal title="Record Stock Movement" open={modalOpen} onCancel={() => { setModalOpen(false); form.resetFields(); }} footer={null} width={520}>
        <Form form={form} layout="vertical" onFinish={(vals) => createMut.mutate(vals)} className="mt-4">
          <Form.Item name="part_id" label="Part" rules={[{ required: true }]}>
              <Select showSearch placeholder="Select part…" filterOption={(v, o) => ((o?.label as string) ?? "").toLowerCase().includes(v.toLowerCase())}
                options={allParts.map((p) => ({ value: p.id, label: `${p.name}${p.sku ? ` (${p.sku})` : ""}` }))} />
            </Form.Item>
          <Form.Item name="movement_type" label="Movement Type" rules={[{ required: true }]}>
            <Select options={["purchase", "job_usage", "transfer", "adjustment", "return", "write_off"].map((v) => ({ value: v, label: v.replace("_", " ") }))} />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="from_location_id" label="From Location">
              <Select allowClear options={locations.map((l: any) => ({ value: l.id, label: l.name }))} placeholder="Select location" />
            </Form.Item>
            <Form.Item name="to_location_id" label="To Location">
              <Select allowClear options={locations.map((l: any) => ({ value: l.id, label: l.name }))} placeholder="Select location" />
            </Form.Item>
          </div>
          <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}><InputNumber className="w-full" min={0.001} step={0.001} /></Form.Item>
          <Form.Item name="unit_cost" label="Unit Cost (£)"><InputNumber className="w-full" min={0} step={0.01} prefix="£" /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending}>Record</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

const InventoryPage = () => {
  const tabItems = [
    { key: "suppliers", label: <span className="flex items-center gap-1.5"><Truck size={13} />Suppliers</span>, children: <SuppliersTab /> },
    { key: "parts", label: <span className="flex items-center gap-1.5"><Package size={13} />Parts</span>, children: <PartsTab /> },
    { key: "levels", label: <span className="flex items-center gap-1.5"><BarChart3 size={13} />Stock Levels</span>, children: <StockLevelsTab /> },
    { key: "locations", label: <span className="flex items-center gap-1.5"><Boxes size={13} />Stock Locations</span>, children: <StockLocationsTab /> },
    { key: "movements", label: <span className="flex items-center gap-1.5"><ArrowDownUp size={13} />Movements</span>, children: <StockMovementsTab /> },
  ];

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-800">Inventory</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage suppliers, parts, stock locations and movements</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <Tabs defaultActiveKey="suppliers" items={tabItems} />
      </div>
    </div>
  );
};

export default InventoryPage;
